import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '../../components/feature/Navigation';
import { getSEOConfig, updateSEOTags } from '../../i18n/utils/seo';
import type { SupportedLanguage } from '../../i18n/utils/language';

export default function JavaTimestamp() {
  const { t, i18n } = useTranslation(['programming', 'common']);
  const [copySuccess, setCopySuccess] = useState('');

  // Update SEO
  useEffect(() => {
    const currentLang = i18n.language as SupportedLanguage;
    const seoConfig = getSEOConfig('javaTimestamp', currentLang);
    updateSEOTags(seoConfig, currentLang);
    document.documentElement.lang = currentLang;
  }, [i18n.language]);

  // Copy to clipboard
  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopySuccess(id);
      setTimeout(() => setCopySuccess(''), 2000);
    });
  };

  const CodeBlock = ({ code, language = 'java', id }: { code: string; language?: string; id: string }) => (
    <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto relative group">
      <button
        onClick={() => copyToClipboard(code, id)}
        className="absolute top-2 right-2 bg-gray-700 hover:bg-gray-600 text-white px-3 py-1.5 rounded text-xs transition-colors opacity-0 group-hover:opacity-100"
      >
        {copySuccess === id ? t('common:button.copied') : t('common:button.copy')}
      </button>
      <pre className="text-green-400 text-sm font-mono whitespace-pre overflow-x-auto">
        {code}
      </pre>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="max-w-5xl mx-auto px-4 py-4 md:py-8">
        {/* Page Header */}
        <div className="text-center mb-6 md:mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            ☕ {t('java.mainTitleSEO')}
          </h1>
          <p className="text-lg text-gray-600">{t('java.subtitle')}</p>
        </div>

        {/* Introduction */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <p className="text-gray-700 leading-relaxed">{t('java.intro')}</p>
        </div>

        {/* Get Current Timestamp */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('java.getCurrentTitle')}</h2>
          <p className="text-gray-600 mb-6">{t('java.getCurrentDesc')}</p>

          <div className="space-y-6">
            {/* System.currentTimeMillis() */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm mr-3">1</span>
                {t('java.method1')}
              </h3>
              <CodeBlock
                id="java-1"
                code={`// Get current timestamp in milliseconds
long timestamp = System.currentTimeMillis();
System.out.println(timestamp);  // Output: 1735948800123

// Convert to seconds (Unix timestamp)
long unixTimestamp = System.currentTimeMillis() / 1000;
System.out.println(unixTimestamp);  // Output: 1735948800

// ⚡ Fastest method - no object creation`}
              />
              <p className="mt-2 text-sm text-gray-600">💡 System.currentTimeMillis() is the fastest method for getting current timestamp</p>
            </div>

            {/* Instant.now() */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm mr-3">2</span>
                {t('java.method2')}
              </h3>
              <CodeBlock
                id="java-2"
                code={`import java.time.Instant;

// Get current timestamp using Instant (Java 8+)
Instant instant = Instant.now();

// Get Unix timestamp in seconds
long epochSecond = instant.getEpochSecond();
System.out.println(epochSecond);  // Output: 1735948800

// Get timestamp in milliseconds
long epochMilli = instant.toEpochMilli();
System.out.println(epochMilli);  // Output: 1735948800123

// ✓ Recommended for new projects - modern API`}
              />
            </div>

            {/* Date class */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm mr-3">3</span>
                {t('java.method3')}
              </h3>
              <CodeBlock
                id="java-3"
                code={`import java.util.Date;

// Using Date class (legacy)
Date date = new Date();
long timestamp = date.getTime();
System.out.println(timestamp);  // Output: 1735948800123

// Convert to seconds
long unixTimestamp = date.getTime() / 1000;
System.out.println(unixTimestamp);  // Output: 1735948800

// ⚠️ Legacy method - prefer Instant for new code`}
              />
            </div>
          </div>
        </div>

        {/* Convert Timestamp to Date */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('java.toDateTitle')}</h2>
          <p className="text-gray-600 mb-6">{t('java.toDateDesc')}</p>

          <div className="space-y-6">
            {/* Instant.ofEpochSecond */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded text-sm mr-3">1</span>
                {t('java.fromTimestamp')}
              </h3>
              <CodeBlock
                id="java-4"
                code={`import java.time.Instant;

// From Unix timestamp (seconds)
long timestamp = 1735948800;
Instant instant = Instant.ofEpochSecond(timestamp);
System.out.println(instant);  // Output: 2025-01-04T04:00:00Z

// From milliseconds
long timestampMs = 1735948800123L;
Instant instant2 = Instant.ofEpochMilli(timestampMs);
System.out.println(instant2);  // Output: 2025-01-04T04:00:00.123Z

// Legacy - from Date
Date date = new Date(timestampMs);
System.out.println(date);  // Output: Sat Jan 04 12:00:00 CST 2025`}
              />
            </div>

            {/* Convert to LocalDateTime */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded text-sm mr-3">2</span>
                {t('java.toLocalDateTime')}
              </h3>
              <CodeBlock
                id="java-5"
                code={`import java.time.Instant;
import java.time.LocalDateTime;
import java.time.ZoneId;

long timestamp = 1735948800;
Instant instant = Instant.ofEpochSecond(timestamp);

// Convert to LocalDateTime (system default timezone)
LocalDateTime ldt = LocalDateTime.ofInstant(instant, ZoneId.systemDefault());
System.out.println(ldt);  // Output: 2025-01-04T12:00:00

// Convert to specific timezone
LocalDateTime ldtBeijing = LocalDateTime.ofInstant(instant, ZoneId.of("Asia/Shanghai"));
System.out.println(ldtBeijing);  // Output: 2025-01-04T12:00:00

LocalDateTime ldtNY = LocalDateTime.ofInstant(instant, ZoneId.of("America/New_York"));
System.out.println(ldtNY);  // Output: 2025-01-03T23:00:00

// Get individual components
int year = ldt.getYear();        // 2025
int month = ldt.getMonthValue(); // 1
int day = ldt.getDayOfMonth();   // 4
int hour = ldt.getHour();        // 12`}
              />
            </div>

            {/* Format with DateTimeFormatter */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded text-sm mr-3">3</span>
                {t('java.formatDate')}
              </h3>
              <CodeBlock
                id="java-6"
                code={`import java.time.Instant;
import java.time.LocalDateTime;
import java.time.ZoneId;
import java.time.format.DateTimeFormatter;

long timestamp = 1735948800;
Instant instant = Instant.ofEpochSecond(timestamp);
LocalDateTime ldt = LocalDateTime.ofInstant(instant, ZoneId.systemDefault());

// Predefined formats
System.out.println(ldt.format(DateTimeFormatter.ISO_LOCAL_DATE_TIME));
// Output: 2025-01-04T12:00:00

System.out.println(ldt.format(DateTimeFormatter.ISO_LOCAL_DATE));
// Output: 2025-01-04

// Custom formats
DateTimeFormatter formatter1 = DateTimeFormatter.ofPattern("yyyy-MM-dd HH:mm:ss");
System.out.println(ldt.format(formatter1));
// Output: 2025-01-04 12:00:00

DateTimeFormatter formatter2 = DateTimeFormatter.ofPattern("MMM dd, yyyy hh:mm a");
System.out.println(ldt.format(formatter2));
// Output: Jan 04, 2025 12:00 PM

DateTimeFormatter formatter3 = DateTimeFormatter.ofPattern("EEEE, MMMM dd, yyyy");
System.out.println(ldt.format(formatter3));
// Output: Saturday, January 04, 2025`}
              />
            </div>
          </div>
        </div>

        {/* Convert Date to Timestamp */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('java.toTimestampTitle')}</h2>
          <p className="text-gray-600 mb-6">{t('java.toTimestampDesc')}</p>

          <div className="space-y-6">
            {/* Date.getTime() */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded text-sm mr-3">1</span>
                {t('java.dateToTimestamp')}
              </h3>
              <CodeBlock
                id="java-7"
                code={`import java.util.Date;
import java.util.Calendar;

// From Date object
Date date = new Date();
long timestamp = date.getTime();
System.out.println(timestamp);  // Milliseconds

// From Calendar
Calendar calendar = Calendar.getInstance();
calendar.set(2025, Calendar.JANUARY, 4, 12, 0, 0);
long timestamp2 = calendar.getTimeInMillis();
System.out.println(timestamp2);

// Convert to seconds
long unixTimestamp = timestamp / 1000;
System.out.println(unixTimestamp);`}
              />
            </div>

            {/* Instant methods */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded text-sm mr-3">2</span>
                {t('java.instantToTimestamp')}
              </h3>
              <CodeBlock
                id="java-8"
                code={`import java.time.Instant;
import java.time.LocalDateTime;
import java.time.ZoneId;

// From Instant
Instant instant = Instant.now();
long epochSecond = instant.getEpochSecond();
long epochMilli = instant.toEpochMilli();

System.out.println("Seconds: " + epochSecond);
System.out.println("Milliseconds: " + epochMilli);

// From LocalDateTime
LocalDateTime ldt = LocalDateTime.of(2025, 1, 4, 12, 0, 0);
Instant instant2 = ldt.atZone(ZoneId.systemDefault()).toInstant();
long timestamp = instant2.getEpochSecond();
System.out.println(timestamp);

// From ZonedDateTime
import java.time.ZonedDateTime;
ZonedDateTime zdt = ZonedDateTime.now();
long timestamp2 = zdt.toEpochSecond();
System.out.println(timestamp2);`}
              />
            </div>
          </div>
        </div>

        {/* Timezone Handling */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('java.timezoneTitle')}</h2>
          <p className="text-gray-600 mb-6">{t('java.timezoneDesc')}</p>

          <div className="space-y-6">
            {/* ZoneId */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3">{t('java.zoneIdExample')}</h3>
              <CodeBlock
                id="java-9"
                code={`import java.time.ZoneId;
import java.time.Instant;
import java.time.LocalDateTime;

// Get available zone IDs
System.out.println(ZoneId.getAvailableZoneIds());

// Common zones
ZoneId utc = ZoneId.of("UTC");
ZoneId beijing = ZoneId.of("Asia/Shanghai");
ZoneId newYork = ZoneId.of("America/New_York");
ZoneId tokyo = ZoneId.of("Asia/Tokyo");

// Convert timestamp to different timezones
Instant instant = Instant.now();

LocalDateTime ldtUtc = LocalDateTime.ofInstant(instant, utc);
LocalDateTime ldtBeijing = LocalDateTime.ofInstant(instant, beijing);
LocalDateTime ldtNY = LocalDateTime.ofInstant(instant, newYork);
LocalDateTime ldtTokyo = LocalDateTime.ofInstant(instant, tokyo);

System.out.println("UTC: " + ldtUtc);
System.out.println("Beijing: " + ldtBeijing);
System.out.println("New York: " + ldtNY);
System.out.println("Tokyo: " + ldtTokyo);`}
              />
            </div>

            {/* ZonedDateTime */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3">{t('java.zonedDateTimeExample')}</h3>
              <CodeBlock
                id="java-10"
                code={`import java.time.ZonedDateTime;
import java.time.ZoneId;
import java.time.format.DateTimeFormatter;

// Create ZonedDateTime
ZonedDateTime zdtNow = ZonedDateTime.now();
System.out.println(zdtNow);

// Create with specific timezone
ZonedDateTime zdtBeijing = ZonedDateTime.now(ZoneId.of("Asia/Shanghai"));
System.out.println(zdtBeijing);

// Convert between timezones
ZonedDateTime zdtNY = zdtBeijing.withZoneSameInstant(ZoneId.of("America/New_York"));
System.out.println("Beijing: " + zdtBeijing);
System.out.println("New York: " + zdtNY);

// Format with timezone
DateTimeFormatter formatter = DateTimeFormatter.ofPattern("yyyy-MM-dd HH:mm:ss z");
System.out.println(zdtBeijing.format(formatter));
// Output: 2025-01-04 12:00:00 CST

// Get Unix timestamp
long timestamp = zdtNow.toEpochSecond();
System.out.println(timestamp);`}
              />
            </div>
          </div>
        </div>

        {/* Common Patterns */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">{t('java.patternsTitle')}</h2>

          <div className="space-y-6">
            {/* Date Arithmetic */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3">{t('java.pattern1.title')}</h3>
              <p className="text-sm text-gray-600 mb-3">{t('java.pattern1.desc')}</p>
              <CodeBlock
                id="java-11"
                code={`import java.time.LocalDateTime;
import java.time.Duration;
import java.time.Period;
import java.time.temporal.ChronoUnit;

LocalDateTime now = LocalDateTime.now();

// Add time using Duration (for hours, minutes, seconds)
LocalDateTime future1 = now.plus(Duration.ofHours(2));
LocalDateTime future2 = now.plus(Duration.ofMinutes(30));
LocalDateTime future3 = now.plus(Duration.ofDays(1));

// Add time using Period (for days, months, years)
LocalDateTime future4 = now.plus(Period.ofDays(7));
LocalDateTime future5 = now.plus(Period.ofMonths(1));
LocalDateTime future6 = now.plus(Period.ofYears(1));

// Using plus methods
LocalDateTime tomorrow = now.plusDays(1);
LocalDateTime nextWeek = now.plusWeeks(1);
LocalDateTime nextMonth = now.plusMonths(1);

// Subtract time
LocalDateTime past = now.minus(Duration.ofHours(1));
LocalDateTime yesterday = now.minusDays(1);

// Using ChronoUnit
LocalDateTime future7 = now.plus(2, ChronoUnit.HOURS);
LocalDateTime future8 = now.plus(30, ChronoUnit.MINUTES);

System.out.println("Now: " + now);
System.out.println("Tomorrow: " + tomorrow);
System.out.println("Next week: " + nextWeek);`}
              />
            </div>

            {/* Parse Date String */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3">{t('java.pattern2.title')}</h3>
              <p className="text-sm text-gray-600 mb-3">{t('java.pattern2.desc')}</p>
              <CodeBlock
                id="java-12"
                code={`import java.time.LocalDateTime;
import java.time.format.DateTimeFormatter;
import java.time.Instant;
import java.time.ZoneId;

// Parse ISO 8601 format
String isoString = "2025-01-04T12:00:00";
LocalDateTime ldt1 = LocalDateTime.parse(isoString);
System.out.println(ldt1);

// Parse custom format
String dateString = "2025-01-04 12:00:00";
DateTimeFormatter formatter = DateTimeFormatter.ofPattern("yyyy-MM-dd HH:mm:ss");
LocalDateTime ldt2 = LocalDateTime.parse(dateString, formatter);
System.out.println(ldt2);

// Different formats
DateTimeFormatter fmt1 = DateTimeFormatter.ofPattern("dd/MM/yyyy");
LocalDateTime ldt3 = LocalDateTime.parse("04/01/2025 12:00",
    DateTimeFormatter.ofPattern("dd/MM/yyyy HH:mm"));

DateTimeFormatter fmt2 = DateTimeFormatter.ofPattern("MMM dd, yyyy");
LocalDateTime ldt4 = LocalDateTime.parse("Jan 04, 2025 12:00 PM",
    DateTimeFormatter.ofPattern("MMM dd, yyyy hh:mm a"));

// Convert to timestamp
Instant instant = ldt2.atZone(ZoneId.systemDefault()).toInstant();
long timestamp = instant.getEpochSecond();
System.out.println("Timestamp: " + timestamp);`}
              />
            </div>

            {/* Compare Timestamps */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3">{t('java.pattern3.title')}</h3>
              <p className="text-sm text-gray-600 mb-3">{t('java.pattern3.desc')}</p>
              <CodeBlock
                id="java-13"
                code={`import java.time.LocalDateTime;
import java.time.Duration;
import java.time.temporal.ChronoUnit;

LocalDateTime dt1 = LocalDateTime.of(2025, 1, 4, 12, 0);
LocalDateTime dt2 = LocalDateTime.of(2025, 1, 6, 15, 30);

// Compare dates
if (dt1.isBefore(dt2)) {
    System.out.println("dt1 is before dt2");
}

if (dt2.isAfter(dt1)) {
    System.out.println("dt2 is after dt1");
}

if (dt1.isEqual(dt2)) {
    System.out.println("Dates are equal");
}

// Calculate difference
Duration duration = Duration.between(dt1, dt2);

long days = duration.toDays();
long hours = duration.toHours();
long minutes = duration.toMinutes();
long seconds = duration.getSeconds();

System.out.println("Days: " + days);
System.out.println("Hours: " + hours);
System.out.println("Minutes: " + minutes);

// Using ChronoUnit for specific units
long daysBetween = ChronoUnit.DAYS.between(dt1, dt2);
long hoursBetween = ChronoUnit.HOURS.between(dt1, dt2);
long minutesBetween = ChronoUnit.MINUTES.between(dt1, dt2);

System.out.println("Days between: " + daysBetween);
System.out.println("Hours between: " + hoursBetween);
System.out.println("Minutes between: " + minutesBetween);`}
              />
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8 border-2 border-yellow-200">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            ⚡ {t('common.bestPractices')}
          </h2>
          <ul className="space-y-3">
            <li className="flex items-start">
              <span className="text-yellow-600 mr-2">✓</span>
              <span className="text-gray-700">Use <code className="bg-gray-100 px-1.5 py-0.5 rounded">java.time</code> API (Java 8+) instead of legacy Date/Calendar classes</span>
            </li>
            <li className="flex items-start">
              <span className="text-yellow-600 mr-2">✓</span>
              <span className="text-gray-700">Use <code className="bg-gray-100 px-1.5 py-0.5 rounded">Instant</code> for machine timestamps and <code className="bg-gray-100 px-1.5 py-0.5 rounded">LocalDateTime</code> for human-readable dates</span>
            </li>
            <li className="flex items-start">
              <span className="text-yellow-600 mr-2">✓</span>
              <span className="text-gray-700">Always store timestamps in UTC and convert to local timezone for display</span>
            </li>
            <li className="flex items-start">
              <span className="text-yellow-600 mr-2">✓</span>
              <span className="text-gray-700">Use <code className="bg-gray-100 px-1.5 py-0.5 rounded">ZonedDateTime</code> when timezone information is critical</span>
            </li>
            <li className="flex items-start">
              <span className="text-yellow-600 mr-2">✓</span>
              <span className="text-gray-700">Prefer <code className="bg-gray-100 px-1.5 py-0.5 rounded">DateTimeFormatter</code> over SimpleDateFormat (thread-safe)</span>
            </li>
            <li className="flex items-start">
              <span className="text-yellow-600 mr-2">✓</span>
              <span className="text-gray-700">Use <code className="bg-gray-100 px-1.5 py-0.5 rounded">System.currentTimeMillis()</code> for performance-critical timestamp generation</span>
            </li>
          </ul>
        </div>

        {/* Related Tools */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            {t('common:relatedTools', { defaultValue: 'Related Tools' })}
          </h2>

          <div className="grid md:grid-cols-3 gap-4">
            <a href="/timestamp-converter" className="block p-4 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 transition-colors">
              <h3 className="font-semibold text-gray-800 mb-1">Unix Timestamp Converter</h3>
              <p className="text-sm text-gray-600">Convert timestamps online</p>
            </a>
            <a href="/python-timestamp" className="block p-4 bg-green-50 hover:bg-green-100 rounded-lg border border-green-200 transition-colors">
              <h3 className="font-semibold text-gray-800 mb-1">Python Timestamp Guide</h3>
              <p className="text-sm text-gray-600">datetime, time modules</p>
            </a>
            <a href="/javascript-timestamp" className="block p-4 bg-yellow-50 hover:bg-yellow-100 rounded-lg border border-yellow-200 transition-colors">
              <h3 className="font-semibold text-gray-800 mb-1">JavaScript Timestamp</h3>
              <p className="text-sm text-gray-600">Date object guide</p>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
